import { Spacing } from '@persona-id/design-tokens';
import { motion } from 'framer-motion';
import { useState } from 'react';
import styled, { ThemeProvider } from 'styled-components';

import Divider from 'dashboard/components/Divider';
import {
  FormRegisterChangeEmail,
  FormRegisterChangeEmailData,
} from 'dashboard/forms/FormRegisterChangeEmail';
import Button from 'design-system/components/Button';
import Spacer from 'design-system/components/Spacer';
import { Display, Paragraph } from 'design-system/components/Text';
import { marketingFontTheme } from 'design-system/theme';
import { BASE } from 'design-system/variables/spacing';
import LogoLight from 'images/brand/logo-color-light.svg';

interface DashboardRegisterConfirmViewProps {
  firstName: string;
  email: string;
  resendEmail: () => void;
  updateEmail: (values: FormRegisterChangeEmailData) => void;
}

const DashboardRegisterConfirmView = ({
  firstName,
  email,
  resendEmail,
  updateEmail,
}: DashboardRegisterConfirmViewProps) => {
  const [showChangeEmail, setShowChangeEmail] = useState(false);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1 }}>
      <ThemeProvider theme={marketingFontTheme}>
        <PageContainer>
          <Logo />
          <Divider full />
          <ContentContainer>
            <Display size='sm'>&#127881; Welcome, {firstName}!</Display>
            <Spacer height={5} />
            <Paragraph size='lg'>
              One last thing — as an extra security measure, we sent you an email at <b>{email}</b>{' '}
              to verify this address belongs to you. Just click on the link in this email to access
              your account.
            </Paragraph>
            {showChangeEmail ? (
              <>
                <Divider full />
                <FormRegisterChangeEmail
                  savedEmail={email}
                  submit={updateEmail}
                  cancel={() => setShowChangeEmail(false)}
                />
              </>
            ) : (
              <>
                <Spacer height={4} />
                <Paragraph size='lg'>Can’t find the email in your inbox or spam folder?</Paragraph>
                <Spacer height={1} />
                <Button type='button' width='auto' variant='basic' onClick={resendEmail}>
                  Resend email
                </Button>
                <Spacer height={3} />
                <Paragraph size='lg'>
                  Wrong email address? You can{' '}
                  <ClickableAnchor
                    data-test='form-register__show-change-email'
                    onClick={() => setShowChangeEmail(true)}
                  >
                    change your email address
                  </ClickableAnchor>
                  .
                </Paragraph>
              </>
            )}
            <Spacer height={4} />
          </ContentContainer>
        </PageContainer>
      </ThemeProvider>
    </motion.div>
  );
};

const PageContainer = styled('div')`
  position: relative;
  background-color: ${({ theme }) => theme.color.primary.white};
  width: 100%;
  height: 100%;
  min-height: 100vh;
`;

const Logo = styled(LogoLight)`
  width: 128px;
  height: auto;
  margin-top: ${Spacing[4]};
  margin-left: ${Spacing[5]};
  margin-bottom: -${Spacing[2]};
`;

const ContentContainer = styled('div')`
  max-width: 1024px;
  margin: auto;
  padding-top: ${Spacing[7]};
  padding-bottom: ${BASE * 10}px;
  padding-left: ${Spacing[5]};
  padding-right: ${Spacing[5]};
`;

const ClickableAnchor = styled.a`
  cursor: pointer;
`;

export default DashboardRegisterConfirmView;
